import { create, type StoreApi, type UseBoundStore } from 'zustand'
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware'
import { getApiClient, useWorkflowStore } from '../../../stores'
import { reconfigureBot, publishBotViaBotJson, findAutonomousNode } from '../services/onboarding'
import { useCases } from '../components'
import orignalBotJson from '../../../../public/default_bot_assets/bot.json'
import _ from 'lodash'
import { trackEvent } from '~/providers'
import { BotUseCase, IDENTITY_WEBSITE_WORKFLOW, IDENTITY_TEXT_WORKFLOW } from '@bpinternal/const'
import { createPollIndexingFileSlice, PollFilesActions, PollFilesState } from './pollIndexingFilesSlice'

const POLLING_START_PERCENT = 15
const POLLING_END_PERCENT = 80
const POLLING_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const POLLING_INTERVAL_MS = 2000 // 2 seconds
const NUMBER_WORKFLOW_CHILDREN = 5

export const IDENTITY_WORKFLOW_CHILD_DESCRIPTION: Record<number, string> = {
  0: 'Finding the links we need to get started',
  1: 'Gathering information about your business',
  2: 'Setting up your bot’s identity',
  3: 'Defining instructions for your bot',
  4: 'Customizing appearance',
  5: 'Getting your bot online',
}

type CheckboxTreeState = {
  urls: string[]
  checked: string[]
  expanded: string[]
}

export type FileStatus = 'pending' | 'uploading' | 'success' | 'error' | 'deleting'

export type UploadableFile = {
  id: string
  fileName: string
  fileType: string
  fileSize: number
  progress: number
  status: FileStatus
  error?: string
  serverFileId?: string
}

export type BusinessSource = 'website' | 'richText'

export type DeploymentStep =
  | 'idle'
  | 'identity-start-requested'
  | 'starting-identity-workflow'
  | 'polling-identity-workflow'
  | 'bot-configuration'
  | 'publishing-bot'
  | 'completed'
  | 'acknowledged'

type IdentityStatus = 'idle' | 'in-progress' | 'completed' | 'cancelled' | 'error'

type DeploymentProgress = {
  currentStep: DeploymentStep
  progress: number
  stepDescription?: string
}

export type NewBotState = {
  startTime: number
  name: string
  userPrompt: string
  business: {
    websiteUrl: string
    richText: string
  }
  useCaseId?: BotUseCase
  knowledgeBase: {
    inputUrl: string
    uploadableFiles: UploadableFile[]
  }
  websiteUrlsTree: CheckboxTreeState
  deploymentProgress: DeploymentProgress
  deploymentWorkflowId?: string
  onboardingStep: number
  isDeploying: boolean
  identityWorkflowId?: string
  identityStatus: IdentityStatus
  identityData?: IdentityWorkflowOutput['identity']
  identityUrls: string[]
}

type Business = {
  name?: string | null
  industry?: string | null
  description?: string | null
  tagline?: string | null
  website?: string | null
  locations: string[] // defaults to [] if not provided
  phone?: string | null
  email?: string | null
  hoursOfOperation?: string | null
  languages?: string[]
  certifications: string[] // defaults to [] if not provided
}

type WebchatTheme = {
  primaryColor?: string
  borderRadius?: number
  theme?: 'light' | 'dark'
  logoUrl?: string
}

export type IdentityWorkflowOutput = {
  identity: {
    identityUrls?: string[]
    improvedBusinessDescription?: string
    projectName: string
    summary: string
    business: Business
    systemePrompt: string
    webchatTheme: WebchatTheme
    webchatDisplayName: string
    webchatDescription: string
  }
}

export type NewBotActions = {
  setName: (name: string) => void
  setUserPrompt: (description: string) => void
  setBusiness: (business: { websiteUrl: string; richText: string }) => void
  setUseCaseId: (useCaseId: BotUseCase) => void
  setWebsiteUrlsTree: (websiteUrlsTree: CheckboxTreeState) => void
  setOnboardingStep: (step: number) => void
  setDeploymentProgress: (step: DeploymentProgress) => void
  setDeploymentWorkflowId: (id: string) => void
  setKnowledgeBaseInputUrl: (url: string) => void
  addUploadableFile: (file: UploadableFile) => void
  removeUploadableFile: (id: string) => void
  updateUploadableFile: (id: string, updates: Partial<UploadableFile>) => void
  clearAllFiles: () => void
  setIdentityData: (data: IdentityWorkflowOutput['identity']) => void
  setIdentityUrls: (urls: string[]) => void
  startIdentityWorkflow: (urls: string[], fallbackDescription?: string) => void
  continueIdentityWorkflow: () => Promise<void>
}

export type StoreState = NewBotActions & NewBotState & PollFilesActions & PollFilesState

const botJson = _.cloneDeep(orignalBotJson)
const storeInstances: Record<string, UseBoundStore<StoreApi<StoreState>>> = {}

// TODO: We could seperate this store into different slices (like createPollIndexingFileSlice)
export const getUseBotOnboardingStore = ({ botId, workspaceId }: { botId: string; workspaceId: string }) => {
  if (!storeInstances[botId]) {
    storeInstances[botId] = create<StoreState>()(
      persist(
        subscribeWithSelector((set, get) => ({
          ...createPollIndexingFileSlice(set),
          startTime: Date.now(),
          name: '',
          userPrompt: '',
          business: {
            websiteUrl: '',
            richText: '',
          },
          websiteUrlsTree: {
            urls: [],
            checked: [],
            expanded: [],
          },
          useCaseId: undefined,
          knowledgeBase: {
            inputUrl: '',
            uploadableFiles: [],
          },
          onboardingStep: 1,
          deploymentProgress: {
            currentStep: 'idle',
            progress: 0,
            stepDescription: undefined,
          },
          deploymentWorkflowId: undefined,
          isDeploying: false,
          identityWorkflowId: undefined,
          identityStatus: 'idle',
          identityData: undefined,
          identityUrls: [],
          setName: (name) => set({ name }),
          setUserPrompt: (userPrompt) => set({ userPrompt }),
          setBusiness: (business) => set({ business }),
          setWebsiteUrlsTree: (websiteUrlsTree) => set({ websiteUrlsTree }),
          setUseCaseId: (useCaseId) => set({ useCaseId }),
          setOnboardingStep: (step: number) => set({ onboardingStep: step }),
          setDeploymentProgress: (progress) =>
            set({
              deploymentProgress: progress,
              isDeploying: !['idle', 'completed', 'acknowledged', 'error'].includes(progress.currentStep),
            }),
          setDeploymentWorkflowId: (id) => set({ deploymentWorkflowId: id }),
          setKnowledgeBaseInputUrl: (url) =>
            set((state) => ({
              knowledgeBase: {
                ...state.knowledgeBase,
                inputUrl: url,
              },
            })),
          addUploadableFile: (file) =>
            set((state) => ({
              knowledgeBase: {
                ...state.knowledgeBase,
                uploadableFiles: [...state.knowledgeBase.uploadableFiles, file],
              },
            })),
          removeUploadableFile: (id) =>
            set((state) => ({
              knowledgeBase: {
                ...state.knowledgeBase,
                uploadableFiles: state.knowledgeBase.uploadableFiles.filter((f) => f.id !== id),
              },
            })),
          updateUploadableFile: (id, updates) =>
            set((state) => ({
              knowledgeBase: {
                ...state.knowledgeBase,
                uploadableFiles: state.knowledgeBase.uploadableFiles.map((f) =>
                  f.id === id ? { ...f, ...updates } : f
                ),
              },
            })),
          clearAllFiles: () =>
            set((state) => ({
              knowledgeBase: {
                ...state.knowledgeBase,
                uploadableFiles: [],
              },
            })),
          setIdentityData: (data) => {
            set({ identityData: data })
          },
          setIdentityUrls: (urls) => {
            set({ identityUrls: urls })
          },
          startIdentityWorkflow: (urls, fallbackDescription) => {
            set({
              identityUrls: urls,
              deploymentProgress: {
                currentStep: 'identity-start-requested',
                progress: 0,
              },
            })
            if (fallbackDescription) {
              set({
                business: {
                  websiteUrl: '',
                  richText: fallbackDescription,
                },
              })
            }
            get().continueIdentityWorkflow()
          },
          continueIdentityWorkflow: async () => {
            const workflowHandlers = [
              handleStartRequested,
              handleStartIdentity,
              handlePollingIdentity,
              handleBotConfiguration,
              handleBotPublishing,
              handleIdentityCompleted,
            ]

            try {
              for (const handler of workflowHandlers) {
                await handler({ get, set, workspaceId, botId })
              }
            } catch {
              set({ identityStatus: 'error' })
            }
          },
        })),
        {
          name: `bot-onboarding-storage-${botId}`,
          storage: createJSONStorage(() => sessionStorage),
        }
      )
    )
  }
  return storeInstances[botId]
}

type HandlerInput = {
  get: () => NewBotState
  set: (state: Partial<NewBotState>) => void
  workspaceId: string
  botId: string
}

async function handleStartRequested({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  if (get().deploymentProgress.currentStep !== 'identity-start-requested') return

  const apiClient = getApiClient({ workspaceId, botId })
  set({ startTime: Date.now() })

  const currentWorkflowId = get().identityWorkflowId
  if (currentWorkflowId && get().identityStatus === 'in-progress') {
    await apiClient.updateWorkflow({
      id: currentWorkflowId,
      status: 'cancelled',
    })
  } else {
    set({ identityStatus: 'in-progress' })
  }

  set({
    deploymentProgress: { currentStep: 'starting-identity-workflow', progress: 5 },
  })
}

async function handleStartIdentity({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  if (get().deploymentProgress.currentStep !== 'starting-identity-workflow') return

  const apiClient = getApiClient({ workspaceId, botId })
  const isFromWebsite = get().business.websiteUrl.length > 0

  const { workflow } = await apiClient.createWorkflow({
    name: isFromWebsite ? IDENTITY_WEBSITE_WORKFLOW : IDENTITY_TEXT_WORKFLOW,
    status: 'pending',
    input: {
      useCase: {
        id: get().useCaseId,
        title: useCases[get().useCaseId ?? 'other'].title,
        description: useCases[get().useCaseId ?? 'other'].description,
      },
      urls: isFromWebsite ? get().identityUrls : undefined,
      businessDescription: !isFromWebsite ? get().business.richText : undefined,
    },
  })

  useWorkflowStore.getState().register({ botId, workspaceId, workflow })
  set({
    identityWorkflowId: workflow.id,
    deploymentProgress: {
      currentStep: 'polling-identity-workflow',
      progress: POLLING_START_PERCENT,
    },
    isDeploying: true,
  })
}

async function handlePollingIdentity({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  if (get().deploymentProgress.currentStep !== 'polling-identity-workflow') return

  const apiClient = getApiClient({ workspaceId, botId })
  const currentWorkflowId = get().identityWorkflowId

  if (!currentWorkflowId) {
    set({ identityStatus: 'error' })
    return
  }

  pollWorkflowProgress({ get, set, workspaceId, botId })
  await useWorkflowStore.getState().awaitStatus(currentWorkflowId, ['completed', 'failed', 'cancelled', 'timedout'])
  const workflowStatus = useWorkflowStore.getState().workflows[currentWorkflowId]?.status

  if (workflowStatus === 'cancelled') {
    set({ identityStatus: 'cancelled' })
    return
  }
  if (workflowStatus !== 'completed') {
    set({ identityStatus: 'error' })
    return
  }

  const { workflow } = await apiClient.getWorkflow({ id: currentWorkflowId })
  set({
    identityData: workflow.output.identity,
    deploymentProgress: {
      currentStep: 'bot-configuration',
      progress: POLLING_END_PERCENT,
      stepDescription: IDENTITY_WORKFLOW_CHILD_DESCRIPTION[5],
    },
  })
}

async function handleBotConfiguration({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  if (get().deploymentProgress.currentStep !== 'bot-configuration') return

  const identityData = get().identityData
  if (!identityData) {
    set({ identityStatus: 'error' })
    return
  }
  await reconfigureBot({
    workspaceId,
    botId,
    name: get().name,
    webchatConfig: {
      primaryColor: identityData.webchatTheme?.primaryColor,
      borderRadiusScale: identityData.webchatTheme?.borderRadius,
      themeMode: identityData.webchatTheme?.theme,
      botAvatarUrl: identityData.webchatTheme?.logoUrl,
      botDisplayName: identityData.webchatDisplayName,
      botDescription: '',
    },
  })
  set({
    deploymentProgress: {
      currentStep: 'publishing-bot',
      progress: 90,
      stepDescription: get().deploymentProgress.stepDescription,
    },
  })
}

async function handleBotPublishing({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  if (get().deploymentProgress.currentStep !== 'publishing-bot') return

  const identityData = get().identityData
  if (!identityData) {
    set({ identityStatus: 'error' })
    return
  }

  botJson.settings.id = botId
  botJson.settings.languages = identityData.business?.languages || ['en']

  const autonomousNode = findAutonomousNode(botJson)
  if (autonomousNode) {
    autonomousNode.guidelines.dynamicValue = identityData.systemePrompt || ''
  }
  await publishBotViaBotJson(workspaceId, botId, botJson)
  set({
    deploymentProgress: {
      currentStep: 'completed',
      progress: 100,
      stepDescription: get().deploymentProgress.stepDescription,
    },
  })
}

async function handleIdentityCompleted({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  if (get().deploymentProgress.currentStep !== 'completed') return

  set({ identityStatus: 'completed', isDeploying: false, identityWorkflowId: undefined })

  const durationMs = get().startTime ? Date.now() - get().startTime : 0
  trackEvent({
    type: 'onboarding_deploy_completed',
    botId,
    workspaceId,
    durationMs,
  })
}

const activePolling = new Map<string, boolean>()

async function pollWorkflowProgress({ get, set, workspaceId, botId }: HandlerInput): Promise<void> {
  const apiClient = getApiClient({ workspaceId, botId })
  const startTime = Date.now()
  const identityWorkflowId = get().identityWorkflowId

  const pollingKey = `${workspaceId}-${botId}-${identityWorkflowId}`
  if (activePolling.get(pollingKey)) {
    return
  }

  activePolling.set(pollingKey, true)

  return new Promise<void>((resolve) => {
    let interval: NodeJS.Timeout | null = null

    const cleanup = () => {
      if (interval) {
        clearInterval(interval)
        interval = null
      }
      activePolling.delete(pollingKey)
      resolve()
    }

    interval = setInterval(async () => {
      const isPolling = get().deploymentProgress.currentStep === 'polling-identity-workflow'
      const isCancelled = get().identityStatus === 'cancelled'
      if (!identityWorkflowId || !isPolling || isCancelled) {
        cleanup()
        return
      }

      if (Date.now() - startTime > POLLING_TIMEOUT_MS) {
        console.error(`Workflow polling timed out after ${POLLING_TIMEOUT_MS / 1000} seconds`)
        set({
          deploymentProgress: {
            currentStep: 'idle',
            progress: 0,
          },
          identityStatus: 'error',
        })
        cleanup()
        return
      }

      const stateName = `extract-identity-${botId}`
      try {
        const { state } = await apiClient.getState({ id: identityWorkflowId, name: stateName, type: 'workflow' })
        const { currentChildIndex } = state.payload
        const currentProgress = currentChildIndex / NUMBER_WORKFLOW_CHILDREN

        const newProgressPercent =
          POLLING_START_PERCENT + currentProgress * (POLLING_END_PERCENT - POLLING_START_PERCENT)

        if (currentChildIndex) {
          set({
            deploymentProgress: {
              currentStep: 'polling-identity-workflow',
              progress: newProgressPercent,
              stepDescription: IDENTITY_WORKFLOW_CHILD_DESCRIPTION[currentChildIndex],
            },
          })
        }
      } catch (error) {
        // Ignore, this is expected when the workflow is not yet created
      }
    }, POLLING_INTERVAL_MS)
  })
}
